package dev.jfronny.zerointerest.db

import androidx.room3.EntityInsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.util.appendPlaceholders
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import kotlin.Int
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.collections.MutableList
import kotlin.collections.mutableListOf
import kotlin.reflect.KClass
import kotlin.text.StringBuilder

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "MemberExtensionConflict"])
internal class SummaryDao_Impl(
  __db: RoomDatabase,
) : SummaryDao {
  private val __db: RoomDatabase

  private val __insertAdapterOfSummaryEntity: EntityInsertAdapter<SummaryEntity>

  private val __insertAdapterOfSummaryTransactionEntity:
      EntityInsertAdapter<SummaryTransactionEntity>
  init {
    this.__db = __db
    this.__insertAdapterOfSummaryEntity = object : EntityInsertAdapter<SummaryEntity>() {
      protected override fun createQuery(): String = "INSERT OR REPLACE INTO `SummaryEntity` (`roomId`,`summaryId`,`parentId`) VALUES (?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: SummaryEntity) {
        statement.bindText(1, entity.roomId)
        statement.bindText(2, entity.summaryId)
        statement.bindText(3, entity.parentId)
      }
    }
    this.__insertAdapterOfSummaryTransactionEntity = object : EntityInsertAdapter<SummaryTransactionEntity>() {
      protected override fun createQuery(): String = "INSERT OR REPLACE INTO `SummaryTransactionEntity` (`roomId`,`summaryId`,`transactionId`) VALUES (?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: SummaryTransactionEntity) {
        statement.bindText(1, entity.roomId)
        statement.bindText(2, entity.summaryId)
        statement.bindText(3, entity.transactionId)
      }
    }
  }

  public override suspend fun insertSummary(entities: List<SummaryEntity>): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfSummaryEntity.insert(_connection, entities)
  }

  public override suspend fun insertTransactions(entities: List<SummaryTransactionEntity>): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfSummaryTransactionEntity.insert(_connection, entities)
  }

  public override suspend fun getParents(roomId: String, summaryId: String): List<String> {
    val _sql: String = "SELECT parentId FROM SummaryEntity WHERE roomId = ? AND summaryId = ?"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, roomId)
        _argIndex = 2
        _stmt.bindText(_argIndex, summaryId)
        val _result: MutableList<String> = mutableListOf()
        while (_stmt.step()) {
          val _item: String
          _item = _stmt.getText(0)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getTransactions(roomId: String, summaryIds: List<String>): List<String> {
    val _stringBuilder: StringBuilder = StringBuilder()
    _stringBuilder.append("SELECT transactionId FROM SummaryTransactionEntity WHERE roomId = ")
    _stringBuilder.append("?")
    _stringBuilder.append(" AND summaryId IN (")
    val _inputSize: Int = summaryIds.size
    appendPlaceholders(_stringBuilder, _inputSize)
    _stringBuilder.append(")")
    val _sql: String = _stringBuilder.toString()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, roomId)
        _argIndex = 2
        for (_item: String in summaryIds) {
          _stmt.bindText(_argIndex, _item)
          _argIndex++
        }
        val _result: MutableList<String> = mutableListOf()
        while (_stmt.step()) {
          val _item_1: String
          _item_1 = _stmt.getText(0)
          _result.add(_item_1)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getSummariesForTransactions(roomId: String, transactionIds: List<String>): List<SummaryTransactionEntity> {
    val _stringBuilder: StringBuilder = StringBuilder()
    _stringBuilder.append("SELECT * FROM SummaryTransactionEntity WHERE roomId = ")
    _stringBuilder.append("?")
    _stringBuilder.append(" AND transactionId IN (")
    val _inputSize: Int = transactionIds.size
    appendPlaceholders(_stringBuilder, _inputSize)
    _stringBuilder.append(")")
    val _sql: String = _stringBuilder.toString()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, roomId)
        _argIndex = 2
        for (_item: String in transactionIds) {
          _stmt.bindText(_argIndex, _item)
          _argIndex++
        }
        val _columnIndexOfRoomId: Int = getColumnIndexOrThrow(_stmt, "roomId")
        val _columnIndexOfSummaryId: Int = getColumnIndexOrThrow(_stmt, "summaryId")
        val _columnIndexOfTransactionId: Int = getColumnIndexOrThrow(_stmt, "transactionId")
        val _result: MutableList<SummaryTransactionEntity> = mutableListOf()
        while (_stmt.step()) {
          val _item_1: SummaryTransactionEntity
          val _tmpRoomId: String
          _tmpRoomId = _stmt.getText(_columnIndexOfRoomId)
          val _tmpSummaryId: String
          _tmpSummaryId = _stmt.getText(_columnIndexOfSummaryId)
          val _tmpTransactionId: String
          _tmpTransactionId = _stmt.getText(_columnIndexOfTransactionId)
          _item_1 = SummaryTransactionEntity(_tmpRoomId,_tmpSummaryId,_tmpTransactionId)
          _result.add(_item_1)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun clearSummaries(roomId: String) {
    val _sql: String = "DELETE FROM SummaryEntity WHERE roomId = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, roomId)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun clearSummaryTransactions(roomId: String) {
    val _sql: String = "DELETE FROM SummaryTransactionEntity WHERE roomId = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, roomId)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
